/**
 * Completion calculation utilities
 * Implements the correct completion logic based on story points and work item states
 */

/**
 * Check if a work item is in a completed state
 * @param {string} state - Work item state
 * @returns {boolean} - True if completed
 */
export const isCompletedState = (state) => {
  return ['Closed', 'Done', 'Removed', 'Completed'].includes(state);
};

/**
 * Calculate completion percentage for a single work item (PBI/Bug)
 * @param {Object} item - Work item object
 * @returns {number} - Completion percentage (0-100)
 */
export const calculateWorkItemCompletion = (item) => {
  // If the item itself is completed, return 100%
  if (isCompletedState(item.state)) {
    return 100;
  }

  // If no tasks, return 0% (unless it's completed above)
  if (!item.tasks || item.tasks.length === 0) {
    return 0;
  }

  // Calculate based on tasks
  let totalStoryPoints = 0;
  let completedStoryPoints = 0;
  let totalTasks = 0;
  let completedTasks = 0;

  for (const task of item.tasks) {
    totalTasks++;
    
    // Use task story points if available, otherwise use 1 as default
    const taskStoryPoints = task.storyPoints || 1;
    totalStoryPoints += taskStoryPoints;

    if (isCompletedState(task.state)) {
      completedTasks++;
      completedStoryPoints += taskStoryPoints;
    }
  }

  // If no story points are estimated, use task count
  if (totalStoryPoints === 0) {
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  }

  // Use story points for calculation
  return Math.round((completedStoryPoints / totalStoryPoints) * 100);
};

/**
 * Calculate completion percentage for a feature
 * @param {Object} feature - Feature object with backlogItems
 * @returns {number} - Completion percentage (0-100)
 */
export const calculateFeatureCompletion = (feature) => {
  // If the feature itself is completed, return 100%
  if (isCompletedState(feature.state)) {
    return 100;
  }

  // If no backlog items, return 0% (unless it's completed above)
  if (!feature.items || feature.items.length === 0) {
    return 0;
  }

  let totalStoryPoints = 0;
  let completedStoryPoints = 0;

  for (const item of feature.items) {
    // Calculate story points for this item (including its tasks)
    const itemStoryPoints = item.storyPoints || 0;
    const taskStoryPoints = calculateTaskStoryPoints(item.tasks || []);
    const totalItemStoryPoints = itemStoryPoints + taskStoryPoints;
    
    totalStoryPoints += totalItemStoryPoints;

    // If the item is completed, add all its story points
    if (isCompletedState(item.state)) {
      completedStoryPoints += totalItemStoryPoints;
    } else {
      // Otherwise, add completed portion based on tasks
      const itemCompletion = calculateWorkItemCompletion(item);
      completedStoryPoints += (totalItemStoryPoints * itemCompletion / 100);
    }
  }

  // If no story points, use item count
  if (totalStoryPoints === 0) {
    const totalItems = feature.items.length;
    const completedItems = feature.items.filter(item => isCompletedState(item.state)).length;
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  }

  return Math.round((completedStoryPoints / totalStoryPoints) * 100);
};

/**
 * Calculate completion percentage for an epic
 * @param {Object} epic - Epic object with features
 * @returns {number} - Completion percentage (0-100)
 */
export const calculateEpicCompletion = (epic) => {
  // If the epic itself is completed, return 100%
  if (isCompletedState(epic.state)) {
    return 100;
  }

  // If no features, return 0% (unless it's completed above)
  if (!epic.features || epic.features.length === 0) {
    return 0;
  }

  let totalStoryPoints = 0;
  let completedStoryPoints = 0;

  for (const feature of epic.features) {
    // Calculate story points for this feature (including its descendants)
    const featureStoryPoints = calculateFeatureStoryPoints(feature);
    totalStoryPoints += featureStoryPoints;

    // If the feature is completed, add all its story points
    if (isCompletedState(feature.state)) {
      completedStoryPoints += featureStoryPoints;
    } else {
      // Otherwise, add completed portion based on its completion
      const featureCompletion = calculateFeatureCompletion(feature);
      completedStoryPoints += (featureStoryPoints * featureCompletion / 100);
    }
  }

  // If no story points, use feature count
  if (totalStoryPoints === 0) {
    const totalFeatures = epic.features.length;
    const completedFeatures = epic.features.filter(feature => isCompletedState(feature.state)).length;
    return totalFeatures > 0 ? Math.round((completedFeatures / totalFeatures) * 100) : 0;
  }

  return Math.round((completedStoryPoints / totalStoryPoints) * 100);
};

/**
 * Calculate total story points for a feature including all its descendants
 * @param {Object} feature - Feature object
 * @returns {number} - Total story points
 */
export const calculateFeatureStoryPoints = (feature) => {
  let totalStoryPoints = 0;

  if (feature.items && feature.items.length > 0) {
    for (const item of feature.items) {
      // Add item's own story points
      totalStoryPoints += item.storyPoints || 0;
      
      // Add task story points
      totalStoryPoints += calculateTaskStoryPoints(item.tasks || []);
    }
  }

  return totalStoryPoints;
};

/**
 * Calculate total story points for tasks
 * @param {Array} tasks - Array of task objects
 * @returns {number} - Total story points
 */
export const calculateTaskStoryPoints = (tasks) => {
  if (!tasks || tasks.length === 0) return 0;
  
  return tasks.reduce((total, task) => {
    return total + (task.storyPoints || 0);
  }, 0);
};

/**
 * Calculate total story points for an epic including all its descendants
 * @param {Object} epic - Epic object
 * @returns {number} - Total story points
 */
export const calculateEpicStoryPoints = (epic) => {
  let totalStoryPoints = 0;

  if (epic.features && epic.features.length > 0) {
    for (const feature of epic.features) {
      totalStoryPoints += calculateFeatureStoryPoints(feature);
    }
  }

  return totalStoryPoints;
};

/**
 * Calculate completed story points for an epic including all its descendants
 * @param {Object} epic - Epic object
 * @returns {number} - Completed story points
 */
export const calculateEpicCompletedStoryPoints = (epic) => {
  let completedStoryPoints = 0;

  if (epic.features && epic.features.length > 0) {
    for (const feature of epic.features) {
      const featureStoryPoints = calculateFeatureStoryPoints(feature);
      
      if (isCompletedState(feature.state)) {
        completedStoryPoints += featureStoryPoints;
      } else {
        const featureCompletion = calculateFeatureCompletion(feature);
        completedStoryPoints += (featureStoryPoints * featureCompletion / 100);
      }
    }
  }

  return completedStoryPoints;
};
